import React from 'react';
import { motion } from 'framer-motion';

interface AudioWaveIndicatorProps {
  isPlaying: boolean;
  barCount?: number;
  color?: string;
}

export const AudioWaveIndicator: React.FC<AudioWaveIndicatorProps> = ({
  isPlaying,
  barCount = 5,
  color = 'rgb(59, 130, 246)' // blue-500
}) => {
  const bars = Array.from({ length: barCount }, (_, i) => i);

  return (
    <div className="flex items-end justify-center gap-1 h-6 px-3 py-1 bg-white rounded-full shadow-lg">
      {bars.map((i) => (
        <motion.div
          key={i}
          className="w-1 rounded-full"
          style={{ backgroundColor: color }}
          animate={isPlaying ? {
            height: ['8px', '20px', '12px', '24px', '8px'],
          } : {
            height: '8px'
          }}
          transition={isPlaying ? {
            duration: 0.8 + (i * 0.1),
            repeat: Infinity,
            ease: 'easeInOut',
            delay: i * 0.1
          } : {
            duration: 0.2
          }}
        />
      ))}
    </div>
  );
};

/**
 * Larger wave indicator for prominent display
 */
export const LargeAudioWaveIndicator: React.FC<AudioWaveIndicatorProps> = ({
  isPlaying,
  barCount = 7,
  color = 'rgb(59, 130, 246)'
}) => {
  const bars = Array.from({ length: barCount }, (_, i) => i);

  return (
    <div className="flex items-end justify-center gap-1.5 h-12">
      {bars.map((i) => {
        // Create a wave pattern - middle bars are taller
        const centerDistance = Math.abs(i - Math.floor(barCount / 2));
        const maxHeight = 40 - centerDistance * 6;
        const minHeight = 12 - centerDistance * 2;

        return (
          <motion.div
            key={i}
            className="w-1.5 rounded-full"
            style={{ backgroundColor: color }}
            animate={isPlaying ? {
              height: [`${minHeight}px`, `${maxHeight}px`, `${minHeight + 4}px`, `${maxHeight - 8}px`, `${minHeight}px`],
            } : {
              height: `${minHeight}px`
            }}
            transition={isPlaying ? {
              duration: 0.6 + (centerDistance * 0.1),
              repeat: Infinity,
              ease: 'easeInOut',
              delay: i * 0.08
            } : {
              duration: 0.3
            }}
          />
        );
      })}
    </div>
  );
};
